<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref, nextTick, computed } from 'vue';
import { startApp, destroyApp } from 'wujie';
import { InstanceofPlugin, SelectionPlugin, EventTargetPlugin } from 'wujie-polyfill';
import type { HiroEntryService, HiroBasicConfig, HostProps } from '../types';

const props = defineProps<{
    service: HiroEntryService;
}>();

const _getHiroProps = () => props.service.getHiroProps();

const containerRef = ref<HTMLElement>();
const hiroBasicConfig = ref<HiroBasicConfig | null>(null);
const wujieProps = ref<HostProps | null>(null);

const globalProxy = computed(() => {
    return wujieProps.value?.proxy.global;
});

const _onLoaded = () => {
    if (_getHiroProps().defaultMenu && globalProxy.value?.rpcHiro.isRegistered('setCurrentMenu')) {
        globalProxy.value.rpcHiro.call('setCurrentMenu', _getHiroProps().defaultMenu);
    }
    if (_getHiroProps().defaultAgentCode && globalProxy.value?.rpcHiro.isRegistered('switchAgent')) {
        globalProxy.value.rpcHiro.call('switchAgent', {
            agentCode: _getHiroProps().defaultAgentCode,
        });
    }
};

onMounted(async() => {
    try {
        hiroBasicConfig.value = await props.service.getHiroBasicConfig();

        if (hiroBasicConfig.value && hiroBasicConfig.value.enabled) {
            wujieProps.value = props.service.getWujieProps(hiroBasicConfig.value);
            if (_getHiroProps().agentCodeScope) {
                // enabledAgentCodes 是开启权限的智能体 code，这里过滤掉不支持的智能体
                wujieProps.value.enabledAgentCodes = wujieProps.value.enabledAgentCodes.filter(code => _getHiroProps().agentCodeScope?.includes(code));
            }

            // 等待 DOM 更新
            await nextTick();

            if (containerRef.value) {

                // 将 containerRef 挂载到 document.body 的最后
                document.body.appendChild(containerRef.value);

                // !!! WujieVue 在 workspace 里无法正常工作，这里直接使用 wujie 的 startApp 方法
                startApp({
                    name: hiroBasicConfig.value.name,
                    url: hiroBasicConfig.value.url,
                    el: containerRef.value,
                    props: wujieProps.value,
                    plugins: [
                        InstanceofPlugin(),
                        SelectionPlugin(),
                        EventTargetPlugin(),
                    ],
                    alive: _getHiroProps().alive ?? true,
                    sync: false,
                });
            }
        }

        globalProxy.value?.eventProxy.on('loaded', _onLoaded);
        _onLoaded();
    } catch (error) {
        console.error('Failed to load Hiro basic config:', error);
    }
});

onBeforeUnmount(() => {
    // 移除事件监听器
    globalProxy.value?.eventProxy.off('loaded', _onLoaded);

    // 销毁 wujie 实例
    if (!(_getHiroProps().alive ?? true) && hiroBasicConfig.value?.name) {
        destroyApp(hiroBasicConfig.value.name);
    }

    // 清理 DOM 元素
    if (containerRef.value && containerRef.value.parentNode === document.body) {
        document.body.removeChild(containerRef.value);
    }
});

</script>

<template>
    <div
        ref="containerRef"
        class="hiro-entry"
    />
</template>

<style lang="scss">
.hiro-entry {
    position: relative;
    z-index: 9999;
}
</style>
